import { announceCartFeedback, getCartErrorMessage } from '@theme/cart-feedback';

/**
 * <s-quick-add> — "+ Add" pill that becomes a −/qty/+ stepper once the variant is in the cart.
 *
 * Adds with POST /cart/add.js and changes quantity with POST /cart/change.js, then asks every
 * mounted <s-cart> to re-fetch its partials so the header count/total and cart drawer update.
 * Other cards for the same variant stay in sync via the "quick-add:change" document event.
 *
 * Required child markup:
 *   [data-quick-add]     — The "+ Add" button.
 *   [data-quick-stepper] — Stepper wrapper (role="group").
 *   [data-quick-minus], [data-quick-plus] — Stepper buttons.
 *   [data-quick-qty]     — Quantity text (polite live region).
 *
 * Host attributes:
 *   data-variant-id — Variant to add.
 *   data-quantity   — Quantity of this variant already in the cart (rendered by Liquid).
 */
class SQuickAdd extends HTMLElement {
  #ac;
  #busy = false;

  get #variantId() {
    return Number(this.dataset.variantId);
  }

  get #quantity() {
    return Number.parseInt(this.dataset.quantity || '0', 10) || 0;
  }

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('[data-quick-add]')) this.#add();
      else if (target.closest('[data-quick-plus]')) this.#change(this.#quantity + 1);
      else if (target.closest('[data-quick-minus]')) this.#change(this.#quantity - 1);
    }, opts);
    document.addEventListener('quick-add:change', (event) => {
      if (event.detail?.source === this || event.detail?.variantId !== this.#variantId) return;
      this.#render(event.detail.quantity, false);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #add() {
    await this.#request('/cart/add.js', { items: [{ id: this.#variantId, quantity: 1 }] }, (data) => {
      const item = Array.isArray(data?.items) ? data.items.find((line) => line.variant_id === this.#variantId) : null;
      return item ? item.quantity : this.#quantity + 1;
    });
  }

  async #change(quantity) {
    const next = Math.max(0, quantity);
    await this.#request('/cart/change.js', { id: String(this.#variantId), quantity: next }, (data) => {
      if (!Array.isArray(data?.items)) return next;
      return data.items
        .filter((line) => line.variant_id === this.#variantId)
        .reduce((sum, line) => sum + line.quantity, 0);
    });
  }

  async #request(path, body, quantityFrom) {
    if (this.#busy) return;
    this.#busy = true;
    this.#setBusy(true);
    const root = window.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(`${root}${path.replace(/^\//, '')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        const message = data?.description || data?.message || getCartErrorMessage(data) || '';
        if (message) await announceCartFeedback(message, { toast: true });
        return;
      }
      this.#render(quantityFrom(data), true);
      await this.#refreshCart();
    } catch (error) {
      const message = getCartErrorMessage(error);
      if (message) await announceCartFeedback(message, { toast: true });
    } finally {
      this.#busy = false;
      this.#setBusy(false);
    }
  }

  async #refreshCart() {
    const CartElement = customElements.get('s-cart');
    if (CartElement && typeof CartElement.refreshMountedCarts === 'function') {
      try {
        await CartElement.refreshMountedCarts();
      } catch {
        /* The header refreshes on the next navigation if partial rendering fails. */
      }
    }
  }

  #render(quantity, broadcast) {
    const hadFocus = this.contains(document.activeElement);
    this.dataset.quantity = String(quantity);
    const addButton = this.querySelector('[data-quick-add]');
    const stepper = this.querySelector('[data-quick-stepper]');
    const qty = this.querySelector('[data-quick-qty]');
    if (qty) qty.textContent = String(quantity);
    addButton?.classList.toggle('hidden', quantity > 0);
    stepper?.classList.toggle('hidden', quantity === 0);
    if (hadFocus) {
      const focusTarget = quantity > 0 ? this.querySelector('[data-quick-plus]') : addButton;
      if (focusTarget && !this.contains(document.activeElement)) focusTarget.focus();
      else if (focusTarget && document.activeElement?.closest('.hidden')) focusTarget.focus();
    }
    if (broadcast) {
      document.dispatchEvent(new CustomEvent('quick-add:change', {
        detail: { variantId: this.#variantId, quantity, source: this },
      }));
    }
  }

  #setBusy(busy) {
    this.toggleAttribute('aria-busy', busy);
    this.querySelectorAll('button').forEach((button) => {
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    });
  }
}

if (!customElements.get('s-quick-add')) customElements.define('s-quick-add', SQuickAdd);
